import { z } from 'zod';
import { ACCRUAL_METHODS, LEAVE_REQUEST_STATUSES, type AccrualMethod, type LeaveRequestStatus, type LedgerKind } from './enums.js';
import { countryCode, isoDate, reason, uuid } from './schemas.js';

// ---------------------------------------------------------------------------
// Input schemas
// ---------------------------------------------------------------------------
const days = z.number().positive().max(366).multipleOf(0.5, 'Use whole or half days');

export const leaveTypeSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9_]{1,12}$/, 'Up to 12 letters/digits')
      .nullable()
      .optional()
      .or(z.literal('').transform(() => null)),
    description: z.string().trim().max(500).nullable().optional(),
    isPaid: z.boolean().default(true),
    requiresApproval: z.boolean().default(true),
    deductsBalance: z.boolean().default(true),
    allowNegativeBalance: z.boolean().default(false),
    allowHalfDays: z.boolean().default(true),
    maxDaysPerRequest: days.nullable().optional(),
    maxDaysPerYear: days.nullable().optional(),
    applicableCountryCodes: z.array(countryCode).max(250).nullable().optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#3372fb'),
    isPrimary: z.boolean().default(false),
    sortOrder: z.number().int().min(0).max(1000).default(100),
    isActive: z.boolean().optional(),
    requiresDocument: z.boolean().default(false),
    maxConsecutiveDays: days.nullable().optional(),
    minGapDays: z.number().int().min(1).max(365).nullable().optional(),
    minServiceMonths: z.number().int().min(1).max(600).nullable().optional(),
    maxTimesPerCareer: z.number().int().min(1).max(100).nullable().optional(),
    countsCalendarDays: z.boolean().default(false),
    drawsFromTypeId: uuid.nullable().optional(),
  })
  .strict();
export type LeaveTypeInput = z.infer<typeof leaveTypeSchema>;

export const policyEntitlementSchema = z
  .object({
    leaveTypeId: uuid,
    daysPerYear: z.number().min(0).max(366).multipleOf(0.5, 'Use whole or half days'),
    accrualMethod: z.enum(ACCRUAL_METHODS).default('upfront'),
    carryOverMaxDays: z.number().min(0).max(366).multipleOf(0.5).default(0),
    /** fixed = daysPerYear; egypt_annual = by service, age and disability (Egyptian labour law). */
    rule: z.enum(['fixed', 'egypt_annual']).default('fixed'),
  })
  .strict();

export const leavePolicySchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().max(500).nullable().optional(),
    isActive: z.boolean().optional(),
    entitlements: z
      .array(policyEntitlementSchema)
      .max(100)
      .refine((e) => new Set(e.map((x) => x.leaveTypeId)).size === e.length, 'Each leave type may appear once'),
  })
  .strict();
export type LeavePolicyInput = z.infer<typeof leavePolicySchema>;

export const leaveRequestInputSchema = z
  .object({
    leaveTypeId: uuid,
    startDate: isoDate,
    endDate: isoDate,
    startHalf: z.boolean().default(false),
    endHalf: z.boolean().default(false),
    reason: z.string().trim().max(1000).optional(),
    /** Uploaded medical report (see /api/files). */
    documentId: uuid.nullable().optional(),
  })
  .strict()
  .refine((r) => r.endDate >= r.startDate, { message: 'End date must be on or after the start date', path: ['endDate'] })
  .refine((r) => !(r.startDate === r.endDate && r.startHalf && r.endHalf), {
    message: 'For a single half day, choose either morning or afternoon',
    path: ['endHalf'],
  });
export type LeaveRequestInput = z.infer<typeof leaveRequestInputSchema>;

/** Admin recording leave on behalf of an employee. */
export const adminLeaveRequestSchema = z
  .object({
    employeeId: uuid,
    leaveTypeId: uuid,
    startDate: isoDate,
    endDate: isoDate,
    startHalf: z.boolean().default(false),
    endHalf: z.boolean().default(false),
    reason: z.string().trim().max(1000).optional(),
    documentId: uuid.nullable().optional(),
    approveNow: z.boolean().default(false),
  })
  .strict()
  .refine((r) => r.endDate >= r.startDate, { message: 'End date must be on or after the start date', path: ['endDate'] })
  .refine((r) => !(r.startDate === r.endDate && r.startHalf && r.endHalf), {
    message: 'For a single half day, choose either morning or afternoon',
    path: ['endHalf'],
  });

export const reviewSchema = z.object({ comment: z.string().trim().max(1000).optional() }).strict();
export const rejectSchema = z.object({ comment: reason }).strict();
export const cancelSchema = z.object({ reason: z.string().trim().max(1000).optional() }).strict();

export const balanceAdjustSchema = z
  .object({
    employeeId: uuid,
    leaveTypeId: uuid,
    /** Any date inside the leave year to adjust. */
    date: isoDate,
    delta: z
      .number()
      .min(-366)
      .max(366)
      .multipleOf(0.5, 'Use whole or half days')
      .refine((v) => v !== 0, 'Enter a non-zero number of days'),
    reason,
  })
  .strict();

export const setEntitlementSchema = z
  .object({
    employeeId: uuid,
    leaveTypeId: uuid,
    date: isoDate,
    entitled: z.number().min(0).max(366).multipleOf(0.5, 'Use whole or half days'),
    reason,
  })
  .strict();

export const leaveListQuerySchema = z.object({
  status: z.enum(LEAVE_REQUEST_STATUSES).optional(),
  employeeId: uuid.optional(),
  leaveTypeId: uuid.optional(),
  departmentId: uuid.optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------
export interface LeaveTypeDto {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  isPaid: boolean;
  requiresApproval: boolean;
  deductsBalance: boolean;
  allowNegativeBalance: boolean;
  allowHalfDays: boolean;
  maxDaysPerRequest: number | null;
  maxDaysPerYear: number | null;
  applicableCountryCodes: string[] | null;
  color: string;
  isPrimary: boolean;
  sortOrder: number;
  isActive: boolean;
  requiresDocument: boolean;
  maxConsecutiveDays: number | null;
  minGapDays: number | null;
  minServiceMonths: number | null;
  maxTimesPerCareer: number | null;
  countsCalendarDays: boolean;
  drawsFromTypeId: string | null;
  inUse?: boolean;
}

export interface LeavePolicyDto {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  employeeCount: number;
  entitlements: {
    leaveTypeId: string;
    leaveTypeName: string;
    daysPerYear: number;
    accrualMethod: AccrualMethod;
    carryOverMaxDays: number;
    rule: 'fixed' | 'egypt_annual';
  }[];
}

export interface LeaveBalanceDto {
  balanceId: string | null; // null = not created yet (no leave activity in that year)
  employeeId: string;
  leaveTypeId: string;
  leaveTypeName: string;
  color: string;
  isPrimary: boolean;
  deductsBalance: boolean;
  allowNegativeBalance: boolean;
  yearStart: string;
  yearEnd: string;
  accrualMethod: AccrualMethod;
  /** Full-year entitlement. */
  entitled: number;
  /** Earned so far (equals entitled for up-front accrual). */
  accrued: number;
  carriedOver: number;
  adjusted: number;
  used: number;
  pending: number;
  /** accrued + carriedOver + adjusted − used − pending */
  remaining: number;
  /** Rules shown next to the balance. */
  rules?: LeaveRulesSummary;
}

/** The limits of a leave type, for display. */
export interface LeaveRulesSummary {
  requiresDocument: boolean;
  maxConsecutiveDays: number | null;
  minGapDays: number | null;
  minServiceMonths: number | null;
  maxTimesPerCareer: number | null;
  maxDaysPerYear: number | null;
  countsCalendarDays: boolean;
  drawsFromTypeName: string | null;
  /** Days of this type already taken (approved + pending) in this leave year. */
  takenThisYear: number;
  /** Times used during the whole employment (for career limits). */
  timesUsed: number;
  /** First date this type can be used (service requirement), if in the future. */
  availableFrom: string | null;
}

export interface LeaveDayDto {
  date: string;
  fraction: number; // 0, 0.5 or 1 counted
  kind: 'working' | 'weekend' | 'holiday' | 'half_holiday' | 'not_employed';
  holidayName?: string;
}

export interface LeavePreviewDto {
  days: number;
  breakdown: LeaveDayDto[];
  balances: { yearStart: string; yearEnd: string; days: number; available: number; after: number }[];
  errors: string[];
  warnings: string[];
  leaveType: { id: string; name: string; deductsBalance: boolean; requiresApproval: boolean; allowNegativeBalance: boolean; requiresDocument: boolean };
}

export interface LeaveRequestDto {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  leaveTypeId: string;
  leaveTypeName: string;
  color: string;
  startDate: string;
  endDate: string;
  startHalf: boolean;
  endHalf: boolean;
  days: number;
  status: LeaveRequestStatus;
  reason: string | null;
  /** Attached medical report, if any. */
  document: { id: string; filename: string } | null;
  /** Recorded automatically for an absent day, paid from the balance. */
  autoAbsence: boolean;
  requestedBy: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewComment: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  /** Whether the viewer may approve/reject this request. */
  canReview?: boolean;
  /** Whether the viewer may cancel this request. */
  canCancel?: boolean;
}

export interface LedgerEntryDto {
  id: string;
  delta: number;
  kind: LedgerKind;
  reason: string | null;
  leaveRequestId: string | null;
  createdByLabel: string;
  createdAt: string;
}

export interface LeaveCalendarEntry {
  requestId: string;
  employeeId: string;
  employeeName: string;
  leaveTypeName: string;
  color: string;
  status: LeaveRequestStatus;
  date: string;
  fraction: number;
}
